import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { billingService } from '@/services/billingService';

interface CouponRedemptionInputProps {
  onRedemptionSuccess: () => void;
}

type RedemptionStep = 'enter-code' | 'tfa-verification' | 'success';

export const CouponRedemptionInput: React.FC<CouponRedemptionInputProps> = ({
  onRedemptionSuccess,
}) => {
  const location = useLocation();
  const [step, setStep] = useState<RedemptionStep>('enter-code');
  const [code, setCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [creditAmount, setCreditAmount] = useState<number>(0);
  const [tfaMessage, setTfaMessage] = useState<string>('');
  const [welcomePromotionMessage, setWelcomePromotionMessage] = useState<string | null>(null);

  // Check if user came from welcome promotion
  useEffect(() => {
    if (location.state?.fromWelcomePromotion) {
      setWelcomePromotionMessage(
        "We've just emailed you your welcome promotion coupon code! Check your inbox and junk mail, then enter the code below."
      );
      // Clear the message after 10 seconds
      setTimeout(() => setWelcomePromotionMessage(null), 10000);
    }
  }, [location]);

  const handleRedeemCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) {
      setError('Please enter a coupon code');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await billingService.redeemCoupon({ code: code.trim() });

      // Check if TFA is required
      if ((result as any).requires_tfa) {
        setCreditAmount((result as any).credit_amount);
        setTfaMessage((result as any).message);

        // Automatically initiate TFA
        await handleInitiateTfa(code.trim());
      } else {
        // No TFA required, show success
        setSuccess((result as any).message);
        setCode('');
        onRedemptionSuccess();

        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to redeem coupon';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateTfa = async (couponCode: string) => {
    try {
      const result = await billingService.initiateTfa({ couponCode });
      setTfaMessage(result.message);
      setStep('tfa-verification');
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to send verification code';
      setError(errorMessage);
    }
  };

  const handleResendTfa = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await billingService.initiateTfa({ couponCode: code });
      setTfaMessage(result.message + ' (Code resent)');
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to resend verification code';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTfa = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await billingService.verifyTfa({
        couponCode: code,
        verificationCode: verificationCode.trim(),
      });

      // Success!
      setSuccess(result.message);
      setStep('success');
      setCode('');
      setVerificationCode('');

      // Notify parent to refresh balance
      onRedemptionSuccess();

      // Reset to enter-code step after 5 seconds
      setTimeout(() => {
        setSuccess(null);
        setStep('enter-code');
      }, 5000);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to verify code';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTfa = () => {
    setStep('enter-code');
    setVerificationCode('');
    setError(null);
    setTfaMessage('');
  };

  return (
    <div className="bg-white dark:bg-neutral-black border border-neutral-light-gray dark:border-neutral-dark-gray rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-neutral-black dark:text-neutral-white mb-4">
        Redeem Coupon
      </h2>

      {/* Welcome Promotion Message */}
      {welcomePromotionMessage && (
        <div className="bg-blue-100 dark:bg-blue-900/20 border border-blue-400 dark:border-blue-800 text-blue-700 dark:text-blue-400 px-4 py-3 rounded mb-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <span className="text-sm">{welcomePromotionMessage}</span>
          </div>
        </div>
      )}

      {/* Step 1: Enter Coupon Code */}
      {step === 'enter-code' && (
        <form onSubmit={handleRedeemCode} className="space-y-4">
          <div>
            <label
              htmlFor="coupon-code"
              className="block text-sm font-medium text-neutral-black dark:text-neutral-white mb-2"
            >
              Coupon Code
            </label>
            <div className="flex gap-2">
              <input
                id="coupon-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                disabled={loading}
                className="flex-1 px-4 py-2 border border-neutral-light-gray dark:border-neutral-dark-gray rounded-md bg-white dark:bg-neutral-black text-neutral-black dark:text-neutral-white placeholder-neutral-medium-gray focus:outline-none focus:ring-2 focus:ring-brand-teal disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={loading || !code.trim()}
                className="px-6 py-2 bg-brand-teal hover:bg-brand-teal-hover text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Checking...' : 'Redeem'}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-100 dark:bg-green-900/20 border border-green-400 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded">
              {success}
            </div>
          )}

          <p className="text-xs text-neutral-medium-gray dark:text-neutral-medium-gray">
            Have a coupon code? Enter it above to add free credits to your account.
          </p>
        </form>
      )}

      {/* Step 2: TFA Verification */}
      {step === 'tfa-verification' && (
        <div className="space-y-4">
          {/* TFA Info */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-purple-900 dark:text-purple-300">
                  📱 SMS Verification Required
                </p>
                <p className="text-xs text-purple-700 dark:text-purple-400 mt-1">{tfaMessage}</p>
                <p className="text-xs text-purple-700 dark:text-purple-400 mt-2">
                  Redeeming <strong className="font-mono">{code}</strong> for{' '}
                  <strong>${creditAmount.toFixed(2)}</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Verification Code Input */}
          <form onSubmit={handleVerifyTfa}>
            <label
              htmlFor="verification-code"
              className="block text-sm font-medium text-neutral-black dark:text-neutral-white mb-2"
            >
              Verification Code
            </label>
            <div className="flex gap-2">
              <input
                id="verification-code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                disabled={loading}
                maxLength={6}
                className="flex-1 px-4 py-2 border border-neutral-light-gray dark:border-neutral-dark-gray rounded-md bg-white dark:bg-neutral-black text-neutral-black dark:text-neutral-white placeholder-neutral-medium-gray focus:outline-none focus:ring-2 focus:ring-brand-teal disabled:opacity-50 font-mono text-lg tracking-widest"
              />
              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="px-6 py-2 bg-brand-teal hover:bg-brand-teal-hover text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </form>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleResendTfa}
              disabled={loading}
              className="text-sm text-brand-teal hover:text-brand-teal-hover font-medium disabled:opacity-50"
            >
              Resend Code
            </button>
            <button
              onClick={handleCancelTfa}
              disabled={loading}
              className="text-sm text-neutral-medium-gray hover:text-neutral-black dark:hover:text-neutral-white font-medium disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Success */}
      {step === 'success' && success && (
        <div className="bg-green-100 dark:bg-green-900/20 border border-green-400 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>{success}</span>
          </div>
        </div>
      )}
    </div>
  );
};
